import {BACKEND_API_V2} from '../config/config'

/**
 * VAT API helpers, following this repo's existing convention (see
 * src/app/modules/personalorder/components/newComponents/getShopData.ts): plain `fetch` against
 * `BACKEND_API_V2`, parse with `.json()`. Both of these helpers are called from checkout UI that
 * must keep working even when VAT lookups fail, so they NEVER throw — any network error or
 * malformed response resolves to a safe fallback instead.
 */

export interface VatEstimate {
  country_id: number | null
  country_code: string | null
  rate: number
  is_eu: boolean
  /** Backend's single VAT switch (VAT_ENABLED flag AND go-live date). Drives the whole shop VAT UI. */
  vat_active?: boolean
}

const VAT_ESTIMATE_FALLBACK: VatEstimate = {
  country_id: null,
  country_code: null,
  rate: 0,
  is_eu: false,
  vat_active: false,
}

/**
 * `countryId` is optional: omitted, the backend estimates from the caller's IP (shop-entry
 * pricing, Task 2). Passed, it overrides the IP guess with the rate for THAT country — used by
 * the checkout summary (Task 4) once a billing country is actually known/selected, so a member
 * billing to a different country than their IP resolved to sees the right rate above Pay.
 */
export async function getVatEstimate(countryId?: number): Promise<VatEstimate> {
  try {
    const query = countryId != null ? `?country_id=${encodeURIComponent(String(countryId))}` : ''
    const res = await fetch(BACKEND_API_V2 + '/webshop/vat-estimate' + query, {
      method: 'GET',
    })
    const data = await res.json()
    return {
      country_id: data?.country_id ?? null,
      country_code: data?.country_code ?? null,
      rate: data?.rate ?? 0,
      is_eu: data?.is_eu ?? false,
      vat_active: data?.vat_active === true,
    }
  } catch (err) {
    console.log(err)
    return VAT_ESTIMATE_FALLBACK
  }
}

export interface VatValidationResult {
  status: 'valid' | 'invalid' | 'unavailable'
  name?: string
}

const VAT_VALIDATE_FALLBACK: VatValidationResult = {
  status: 'unavailable',
}

export async function validateVatId(
  countryCode: string,
  vatId: string
): Promise<VatValidationResult> {
  try {
    const query = `country_code=${encodeURIComponent(countryCode)}&vat_id=${encodeURIComponent(vatId)}`
    const res = await fetch(BACKEND_API_V2 + '/webshop/vat-validate?' + query, {
      method: 'GET',
    })
    const data = await res.json()
    return {
      status: data?.status ?? 'unavailable',
      ...(data?.name ? {name: data.name} : {}),
    }
  } catch (err) {
    console.log(err)
    return VAT_VALIDATE_FALLBACK
  }
}
